import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { CONTACT_CATEGORIES } from '../../../constants/contactCategories.js';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  category: '',
  message: '',
  privacyAgreed: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\-+() ]*$/;

const CATEGORY_LABELS = Object.fromEntries(
  CONTACT_CATEGORIES.filter((c) => c.value).map((c) => [c.value, c.label])
);

/**
 * 認証済み Hub 内のお問い合わせページ
 */
const HubContactPage = () => {
  const { user, clinic } = useOutletContext();
  const { effectiveIsBeta, isHubSettingsReady } = useBeta();
  const { showToast } = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      name: user?.displayName || prev.name,
      email: user?.email || prev.email,
      organization: clinic?.name || prev.organization,
      phone: clinic?.phone || prev.phone,
    }));
  }, [user?.displayName, user?.email, clinic?.name, clinic?.phone]);

  if (!isHubSettingsReady) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">お問い合わせ</h1>
        </div>
        <div className="hub-main-content hub-enter-panel" data-panel-index="0" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--hub-space-2xl)' }}>
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (effectiveIsBeta) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">お問い合わせ</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0">
            <p style={{ marginBottom: 'var(--hub-space-lg)', color: 'var(--hub-text-secondary)' }}>
              β版期間中はご利用いただけません。
            </p>
            <Link to="/dashboard" className="hub-btn hub-btn-primary">
              ダッシュボードへ戻る
            </Link>
          </div>
        </div>
      </>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'お名前を入力してください。';
    else if (form.name.trim().length > 100) errs.name = '100文字以内で入力してください。';
    if (!form.email.trim()) errs.email = 'メールアドレスを入力してください。';
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = '有効なメールアドレスを入力してください。';
    if (form.phone && !PHONE_RE.test(form.phone)) errs.phone = '電話番号の形式が正しくありません。';
    if (form.organization && form.organization.length > 200) errs.organization = '200文字以内で入力してください。';
    if (!form.category) errs.category = 'お問い合わせ種別を選択してください。';
    if (!form.message.trim()) errs.message = 'お問い合わせ内容を入力してください。';
    else if (form.message.trim().length < 10) errs.message = '10文字以上で入力してください。';
    else if (form.message.trim().length > 5000) errs.message = '5000文字以内で入力してください。';
    if (!form.privacyAgreed) errs.privacyAgreed = 'プライバシーポリシーに同意してください。';
    return errs;
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setConfirming(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError('');
    try {
      const response = await fetch(buildApiUrl('/api/contact/hub'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          organization: form.organization.trim(),
          category: form.category,
          message: form.message.trim(),
          clinicName: clinic?.name || '',
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 429) {
          setServerError('送信回数の制限に達しました。5分後にもう一度お試しください。');
        } else {
          setServerError(data.message || 'お問い合わせの送信に失敗しました。');
        }
        showToast('contact.error', { message: data.message || '送信に失敗しました。' });
        return;
      }
      setSubmitted(true);
      showToast('contact.success');
    } catch {
      setServerError('ネットワークエラーが発生しました。');
      showToast('contact.error', { message: 'ネットワークエラーが発生しました。' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">お問い合わせ</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0" style={{ textAlign: 'center', padding: 'var(--hub-space-2xl)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 'var(--hub-space-lg)' }}>✓</div>
            <h2 style={{ fontFamily: 'var(--hub-font-display)', marginBottom: 'var(--hub-space-md)' }}>送信完了</h2>
            <p style={{ color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-xl)' }}>
              お問い合わせを受け付けました。確認メールをお送りしましたのでご確認ください。
            </p>
            <Link to="/dashboard" className="hub-btn hub-btn-primary">ダッシュボードへ戻る</Link>
          </div>
        </div>
      </>
    );
  }

  if (confirming) {
    return (
      <>
        <div className="hub-main-header">
          <h1 className="hub-page-title">お問い合わせ — 確認</h1>
        </div>
        <div className="hub-main-content">
          <div className="hub-card hub-enter-panel" data-panel-index="0">
            <p style={{ color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-xl)' }}>
              以下の内容で送信します。よろしければ「送信する」を押してください。
            </p>
            <dl style={{ margin: 0 }}>
              {[
                ['お名前', form.name],
                ['メール', form.email],
                ['電話', form.phone || '—'],
                ['クリニック', form.organization || '—'],
                ['種別', CATEGORY_LABELS[form.category] || form.category],
              ].map(([label, value]) => (
                <div key={label} className="hub-contact-confirm-row" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', padding: '10px 0', borderBottom: '1px solid var(--hub-border)' }}>
                  <dt style={{ color: 'var(--hub-text-muted)', fontSize: '0.875rem' }}>{label}</dt>
                  <dd style={{ margin: 0 }}>{value}</dd>
                </div>
              ))}
              <div style={{ padding: '10px 0' }}>
                <dt style={{ color: 'var(--hub-text-muted)', fontSize: '0.875rem', marginBottom: '8px' }}>内容</dt>
                <dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{form.message}</dd>
              </div>
            </dl>
            {serverError && (
              <p style={{ color: 'var(--hub-danger)', marginTop: 'var(--hub-space-lg)' }}>{serverError}</p>
            )}
            <div className="hub-contact-confirm-actions" style={{ display: 'flex', gap: 'var(--hub-space-md)', marginTop: 'var(--hub-space-xl)' }}>
              <button type="button" className="hub-btn hub-btn-ghost" onClick={() => setConfirming(false)} disabled={submitting}>
                修正する
              </button>
              <button type="button" className="hub-btn hub-btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <LoadingSpinner size="small" /> : '送信する'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">お問い合わせ</h1>
      </div>
      <div className="hub-main-content">
        <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1" data-panel-index="0">
          <p style={{ color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-xl)' }}>
            製品のご利用や技術的なご相談など、お気軽にお問い合わせください。通常1〜2営業日以内にご返信いたします。
          </p>

          {serverError && (
            <div style={{
              background: 'var(--hub-danger-light)',
              color: 'var(--hub-danger)',
              padding: 'var(--hub-space-md)',
              borderRadius: 'var(--hub-radius-md)',
              marginBottom: 'var(--hub-space-xl)',
            }}>
              {serverError}
            </div>
          )}

          <form onSubmit={handleConfirm}>
            <div className="hub-form-group">
              <label className="hub-form-label" htmlFor="hub-contact-name">お名前 <span style={{ color: 'var(--hub-danger)' }}>*</span></label>
              <input id="hub-contact-name" name="name" type="text" className="hub-form-input" value={form.name} onChange={handleChange} />
              {errors.name && <span className="hub-form-error">{errors.name}</span>}
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label" htmlFor="hub-contact-email">メールアドレス <span style={{ color: 'var(--hub-danger)' }}>*</span></label>
              <input id="hub-contact-email" name="email" type="email" className="hub-form-input" value={form.email} onChange={handleChange} />
              {errors.email && <span className="hub-form-error">{errors.email}</span>}
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label" htmlFor="hub-contact-phone">電話番号</label>
              <input id="hub-contact-phone" name="phone" type="tel" className="hub-form-input" value={form.phone} onChange={handleChange} />
              {errors.phone && <span className="hub-form-error">{errors.phone}</span>}
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label" htmlFor="hub-contact-org">クリニック名</label>
              <input id="hub-contact-org" name="organization" type="text" className="hub-form-input" value={form.organization} onChange={handleChange} />
              {errors.organization && <span className="hub-form-error">{errors.organization}</span>}
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label" htmlFor="hub-contact-category">お問い合わせ種別 <span style={{ color: 'var(--hub-danger)' }}>*</span></label>
              <select id="hub-contact-category" name="category" className="hub-form-input" value={form.category} onChange={handleChange}>
                {CONTACT_CATEGORIES.map((c) => (
                  <option key={c.value || 'empty'} value={c.value}>{c.label}</option>
                ))}
              </select>
              {errors.category && <span className="hub-form-error">{errors.category}</span>}
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label" htmlFor="hub-contact-message">お問い合わせ内容 <span style={{ color: 'var(--hub-danger)' }}>*</span></label>
              <textarea
                id="hub-contact-message"
                name="message"
                className="hub-form-input"
                rows={6}
                value={form.message}
                onChange={handleChange}
                style={{ resize: 'vertical', minHeight: '120px' }}
              />
              {errors.message && <span className="hub-form-error">{errors.message}</span>}
            </div>

            <div className="hub-form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" name="privacyAgreed" checked={form.privacyAgreed} onChange={handleChange} style={{ marginTop: '4px' }} />
                <span style={{ fontSize: '0.875rem', color: 'var(--hub-text-secondary)' }}>
                  <Link to="/support/legal/privacy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</Link>
                  に同意します <span style={{ color: 'var(--hub-danger)' }}>*</span>
                </span>
              </label>
              {errors.privacyAgreed && <span className="hub-form-error">{errors.privacyAgreed}</span>}
            </div>

            <button type="submit" className="hub-btn hub-btn-primary">確認画面へ</button>
          </form>
        </div>
      </div>
    </>
  );
};

export default HubContactPage;
